<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="isModalOpen" class="modal-overlay" v-bind="$attrs" @click="handleBackdropClick">
        <div ref="content" :class="{ 'default-height': defaultHeight, 'shake': isShaking }" class="modal-content"
        :style="{ '--modal-height':   length + 'px'}">
          <div class="modal-header">
            <div class="modal-intro">
              <span class="material-symbols-outlined">{{ iconModal }}</span>
              <div class="modal-headline">
                <h1>{{ modalTitle }}</h1>
                <div v-if="modalSubtitle.length !== 0" class="modal-subtitle">
                  {{ modalSubtitle }}
                </div>
              </div>
            </div>
            <div class="modal-controls">
              <slot name="header-controls"></slot>
              <span v-if="closingOffered" class="material-symbols-outlined" @click="closeModal">close</span>
            </div>
          </div>
          <div class="modal-body" :style="{ '--padding-top':   (length === 60 ? 30 : 25) + 'px', '--body-gap': bodyGap + 'px' }">
            <slot name="body"></slot>
            <div v-if="discardingOffered || confirmationRequired || $slots.footer" class="modal-footer">
              <slot name="footer"></slot>
              <button v-if="discardingOffered" :disabled="discardingDisabled"
                      class="discard-button final-button"
                      @click="reject">
                <span v-if="iconDiscard" class="material-symbols-outlined btn-icon">{{ iconDiscard }}</span>
                {{ textDiscard }}
              </button>
              <button v-if="confirmationRequired && !confirmationFinished" :disabled="confirmationDisabled"
                      class="confirm-button final-button" :class="{ error: confirmError }"
                      @click.stop="confirm">
                <span v-if="confirmClicked">{{ textConfirmClicked }}</span>
                <span v-else>{{ textConfirm }}</span>
              </button>
              <button v-else-if="confirmationRequired" :class="confirmationSucceeded ? 'success' : 'error'"
                      class="confirm-button final-button" disabled>
                {{ confirmationSucceeded ? textConfirmSuccess : textConfirmError }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import {mapMutations, mapState} from "vuex";

export default {
  name: "BaseModal",
  inheritAttrs: false,
  data (){
    return {
      confirmClicked: false,
      isShaking: false,
    };
  },
  props: {
    isOpen: {
      default: undefined
    },
    modalName: String,
    iconModal: {
      type: String,
    },
    modalTitle: {
      type: String,
    },
    modalSubtitle: {
      type: String,
      default: ""
    },
    textDiscard: {
      type: String,
      default: "Discard"
    },
    textConfirm: {
      type: String,
      default: "Confirm"
    },
    textConfirmSuccess: {
      type: String,
      default: "Success!"
    },
    textConfirmError: {
      type: String,
      default: "Failed!"
    },
    confirmError: {
      type: Boolean,
      default: false
    },
    defaultHeight: {
      type: Boolean,
      default: true
    },
    closingOffered: {
      type: Boolean,
      default: true
    },
    confirmationRequired: {
      type: Boolean,
      default: false
    },
    confirmationDisabled: {
      type: Boolean,
      default: false
    },
    discardingOffered: {
      type: Boolean,
      default: false
    },
    discardingDisabled: {
      type: Boolean,
      default: false
    },
    iconDiscard: {
      type: String,
      default: ''
    },
    closeOnConfirm: {
      type: Boolean,
      default: true
    },
    confirmationFinished: {
      type: Boolean,
      default: false,
    },
    confirmationSucceeded: {
      type: Boolean,
      default: false,
    },
    length:{
      type: Number,
      default: 60,
    },
    bodyGap:{
      type: Number,
      default: 20,
    },
    textConfirmClicked:{
      type: String,
      default: "Confirmed!",
    }
  },
  computed: {
    ...mapState(["currentModal"]),
    isModalOpen() {
      if (this.isOpen !== undefined) {
        return this.isOpen;
      }
      return this.currentModal === this.modalName;
    },
  },
  methods: {
    ...mapMutations(["CLOSE_MODAL"]),
    closeModal() {
      this.$emit("beforeClose");
      this.CLOSE_MODAL();
    },
    handleBackdropClick(event) {
      if (event.target === event.currentTarget) {
        if (this.closingOffered) {
          this.closeModal();
        } else {
          this.isShaking = true;
          setTimeout(() => {
            this.isShaking = false;
          }, 400);
        }
      }
    },
    confirm() {
      this.$emit("confirm");
      if (this.closeOnConfirm) {
        this.closeModal();
      }
      else{
      this.confirmClicked = true;
        setTimeout(() => {
          this.confirmClicked = false;
        }, 1500);
      }
    },
    reject() {
      this.$emit("reject");
      this.closeModal();
    },
  }
};
</script>


<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 990; 
}

.modal-content {
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 8px;
  width: 80%;
  max-width: 700px;
  max-height: 535px;
  color: #333333;
}

.modal-content.default-height {
  height: var(--modal-height);
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-5px); }
  40% { transform: translateX(5px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
}

.modal-content.shake {
  animation: shake 0.4s ease-in-out;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 20px 25px 25px;
  border-bottom: 1px solid #bbb;
}

.modal-intro {
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 18px;
  margin-right: 15px;
}

.modal-intro .material-symbols-outlined {
  font-size: 30px;
  color: #9ABCE4;
}

.modal-headline {
  display: flex;
  flex-direction: column;
}

h1 {
  font-size: 18px;
  margin: 5px 0;
}

.modal-subtitle {
  font-size: 80%;
  line-height: normal;
}

.modal-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  cursor: pointer;
}

.modal-controls .material-symbols-outlined {
  padding: 5px;
  cursor: pointer;
  border-radius: 4px;
}

.modal-controls .material-symbols-outlined:hover {
  background-color: #eee;
}

.modal-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--body-gap);
  padding: 25px;
  padding-top: var(--padding-top);
  overflow: auto;
}

.modal-footer {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 10px;
}

.final-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 16px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.9em;
  font-weight: 500;
  white-space: nowrap;
  transition: background-color 0.3s, color 0.3s, border-color 0.3s;
}

.btn-icon {
  font-size: 18px;
}

.final-button:disabled {
  cursor: auto;
}

.confirm-button {
  color: white;
  border: 1px solid #9ABCE4;
  background-color: #9ABCE4;
}

.confirm-button.success:disabled {
  border-color: #9FBA36;
  background-color: #9FBA36;
}

.confirm-button.error:disabled, .confirm-button.error {
  border-color: #D95117;
  background-color: #D95117;
}

.confirm-button.error:not(:disabled):hover {
  border-color: #B04210;
  background-color: #B04210;
}

.confirm-button:disabled {
  border-color: #DDE2E6;
  background-color: #DDE2E6;
}

.confirm-button:not(:disabled):hover {
  border-color: #5E94D4;
  background-color: #5E94D4;
}

.discard-button {
  color: #9ABCE4;
  border: 1px solid #9ABCE4;
  background-color: white;
}

.discard-button:disabled {
  color: #DDE2E6;
  border-color: #DDE2E6;
}

.discard-button:not(:disabled):hover {
  color: #5E94D4;
  border-color: #5E94D4;
}
</style>